import React, { useState, useEffect } from 'react';
import { Container } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Toast } from 'primereact/toast';

import useToast from '../shared/hooks/useToast';
import DisplayStaff from './DisplayStaff';
import EditStaff from './EditStaff';
import BottomTabs from './BottomTabs';
import AdminEdit from './AdminEdit.js';
import StaffInfoModal from './StaffInfoModal.js';
import withStaffLookups from '../shared/withStaffLookups';

import api from '../../scripts/api';
import { message } from '../shared/Message';
import StaffDto from '../../DTOs/Staff/StaffDto';
import { clientAssignmentType, userRoles } from '../../constants';
import { useParams } from 'react-router';
import ClientAssignmentWarningModal from './ClientAssignmentWarningModal.js';

function Staff({ licenseTypes, staffEducationLevels, ...props }) {
  const [staff, setStaff] = useState(new StaffDto());
  const [showEdit, setShowEdit] = useState(false);
  const [showStaffInfoModal, setShowStaffInfoModal] = useState(false);
  const [showClientAssignmentWarningModal, setShowClientAssignmentWarningModal] = useState(false);
  const [modalConfirmCallback, setModalConfirmCallback] = useState(null);
  const { id } = useParams();
  const effectiveOrganizationId = useSelector(state => state.user.effectiveOrganizationId);
  const currentUserOrganizationId = useSelector(state => state.user.organizationId);
  const { toast, errorToast } = useToast();

  const editSiteAdmin = (currentUserOrganizationId === 1 && effectiveOrganizationId === null);
  const currentUserIsAdmin = props.userPermissions.rolePermission === userRoles.ADMIN;
  const currentUserRole = props.userPermissions.rolePermission;

  const openStaffInfoModal = () => {
    setShowStaffInfoModal(true);
  }

  const closeStaffInfoModal = () => {
    setShowStaffInfoModal(false);
  }

  const handleEditClick = () => {
    setShowEdit(prevState => !prevState);
  }

  const updateUserHandler = (dto, actions) => {
    api.updateStaff(dto.stringify()).then(response => {
      setStaff(response);
      setShowEdit(prevState => !prevState);
    }).catch(err => {
      actions.setSubmitting(false);
      errorToast(`Error submitting staff: ${err}`);
      message.error(`Error submitting staff: ${err}`);
    });
  }

  const editStaffSubmit = (values, actions) => {
    const dto = new StaffDto({
      ...values,
      isDisabled: !values.isActive,
      educationLevel: staffEducationLevels.filter(kw => kw.value === values.educationLevelValue)[0],
      licenseTypes: licenseTypes.filter(kw => kw.value === values.licenseTypeValue),
      clientAssignmentType: Number(values.clientAssignmentType)
    });

    if (dto.clientAssignmentType === clientAssignmentType.ClientsInAssignedPrograms && staff.clientAssignmentType !== dto.clientAssignmentType) {
      setModalConfirmCallback(() => () => updateUserHandler(dto, actions));
      setShowClientAssignmentWarningModal(true);
    } else {
      updateUserHandler(dto, actions);
    }
  }

  const editSiteAdminSubmit = (values, actions) => {
    const dto = new StaffDto({
      ...values,
      organizationId: 1,
      userRoleId: 1,
      isDisabled: !values.isActive,
      educationLevel: props.staffEducationLevels.filter(kw => kw.value === values.educationLevelValue)[0],
      licenseTypes: props.licenseTypes.filter(kw => kw.value === values.licenseTypeValue)
    });

    updateUserHandler(dto, actions);
  }

  const initializeComponent = (staffId) => {
    if (staffId && parseInt(staffId, 10) !== 0) {
      api.getStaffById(staffId).then(result => {
        setStaff(result);
      }).catch(err => {
        let message = `Error getting staff: ${err.message}`;
        errorToast(message);
        message.error(message);
      });
    }
  }

  useEffect(() => {
    initializeComponent(id);
  }, [id])

  return (
    <>
      <Container className="basic-card pb-3 mb-5">
        {!showEdit
          ? <DisplayStaff staff={staff} handleEditClick={handleEditClick} />

          : (editSiteAdmin
            ? <AdminEdit
              staff={staff}
              editAction={"Edit"}
              redirect={false}
              licenseTypes={licenseTypes}
              staffEducationLevels={staffEducationLevels}
              handleCancelEditClick={handleEditClick}
              handleFormSubmit={editSiteAdminSubmit}
              siteAdmin
            />
            : <EditStaff
              staff={staff}
              editAction={"Edit"}
              redirect={false}
              licenseTypes={licenseTypes}
              staffEducationLevels={staffEducationLevels}
              handleStaffInfoClick={openStaffInfoModal}
              handleCancelEditClick={handleEditClick}
              handleFormSubmit={editStaffSubmit}
              currentUserRole={currentUserRole}
            />
          )
        }
        <Toast ref={toast} position="bottom-left" />
      </Container>

      {showStaffInfoModal ?
        <StaffInfoModal
          showModal={showStaffInfoModal}
          hideModal={closeStaffInfoModal}
        />
        : null}

      {staff.userRoleName === userRoles.ADMIN ?
        <React.Fragment />
        :
        <BottomTabs staff={staff} />
      }

      {showClientAssignmentWarningModal ?
        <ClientAssignmentWarningModal
          showModal={showClientAssignmentWarningModal}
          hideModal={() => setShowClientAssignmentWarningModal(false)}
          confirmUpdate={modalConfirmCallback}
        />
        : null
      }

    </>
  )
}

export default withStaffLookups(Staff);
